import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ThemeContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Atelier Suisse Design System
export const atelierSuisse = {
  name: 'Atelier Suisse',
  fonts: {
    headings: "'Chivo', sans-serif",
    body: "'Inter', sans-serif",
    mono: "'JetBrains Mono', monospace"
  },
  colors: {
    primary: '#C2410C',        // International Clay
    primaryLight: '#EA580C',
    primaryDark: '#9A3412',
    accent: '#2563EB',         // Blueprint Blue (links only)
    background: '#FDFBF7',     // Trace Paper
    backgroundDark: '#1C1917', // Obsidian
    card: '#FFFFFF',
    cardDark: '#292524',
    border: '#E7E5E4',
    borderDark: '#44403C',
    text: '#1C1917',
    textDark: '#FAFAF9',
    textMuted: '#78716C',
    textMutedDark: '#A8A29E'
  },
  borderRadius: {
    none: '0px',
    sm: '2px',
    default: '0px'
  },
  shadows: {
    card: '4px 4px 0px 0px rgba(0,0,0,0.08)',
    cardHover: '6px 6px 0px 0px rgba(194,65,12,0.15)',
    cardDark: '4px 4px 0px 0px rgba(0,0,0,0.3)',
    cardHoverDark: '6px 6px 0px 0px rgba(194,65,12,0.3)'
  }
};

// Theme definitions - Ocre & Rouge uniquement
export const themes = {
  // THÈME GRIS - PÉRIODE DE TEST
  gris: {
    name: 'Gris Professionnel',
    // Main colors - Gray scale
    primary: '#4b5563',        // Gray-600
    primaryLight: '#6b7280',   // Gray-500
    primaryDark: '#374151',    // Gray-700
    primaryLighter: '#e5e7eb', // Gray-200
    accent: '#1f2937',         // Gray-800
    accentLight: '#9ca3af',    // Gray-400
    accentDark: '#111827',     // Gray-900
    // Background & surface
    bg: '#f9fafb',             // Gray-50
    bgGradient: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
    card: '#ffffff',
    cardBorder: '#e5e7eb',     // Gray-200
    // Text
    text: '#111827',           // Gray-900
    textMuted: '#6b7280',      // Gray-500
    textLight: '#9ca3af',      // Gray-400
    // Gray colors for UI elements
    warm200: '#e5e7eb',
    warm300: '#d1d5db',
    warm400: '#9ca3af',
    warm500: '#6b7280',
    warm600: '#4b5563',
    warm700: '#374151',
    // Button
    buttonBg: '#4b5563',
    buttonText: '#ffffff',
    buttonHover: '#374151',
    // Dark mode
    dark: {
      bg: '#111827',
      bgGradient: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
      card: '#1f2937',
      cardBorder: '#374151',
      text: '#f9fafb',
      textMuted: '#9ca3af',
      textLight: '#6b7280',
      buttonBg: '#4b5563',
      buttonText: '#ffffff',
    }
  },
  ocre: {
    name: 'Ocre & Rouge',
    // Main colors
    primary: '#c2410c',
    primaryLight: '#ea580c',
    primaryDark: '#9a3412',
    primaryLighter: '#fed7aa',
    accent: '#dc2626',
    accentLight: '#fca5a5',
    accentDark: '#991b1b',
    // Background & surface
    bg: '#fef7f0',
    bgGradient: 'linear-gradient(135deg, #fef7f0 0%, #fde6cc 100%)',
    card: '#ffffff',
    cardBorder: '#fed7aa',
    // Text
    text: '#1c1410',
    textMuted: '#8b5a2b',
    textLight: '#d4956a',
    // Warm colors for UI elements
    warm200: '#fde6cc',
    warm300: '#fed7aa',
    warm400: '#d4956a',
    warm500: '#b87333',
    warm600: '#8b5a2b',
    warm700: '#5c3d1e',
    // Button
    buttonBg: '#c2410c',
    buttonText: '#ffffff',
    buttonHover: '#9a3412',
    // Dark mode - palette bleu nuit inspirée de l'app météo
    dark: {
      bg: '#1A233A',
      bgGradient: 'linear-gradient(135deg, #1A233A 0%, #2D354A 100%)',
      card: '#2D354A',
      cardBorder: '#4A5873',
      text: '#FFFFFF',
      textMuted: '#A8AFBD',
      // Icônes et éléments clairs en mode sombre
      icon: '#FFFFFF',
      iconHover: '#E8EDF5',
    }
  }
};

// For backwards compatibility
export const palette = themes.ocre;

export const ThemeProvider = ({ children }) => {
  // PÉRIODE DE TEST: Forcer le mode clair
  const [darkMode, setDarkMode] = useState(() => {
    // Forcé à false pour la période de test
    return false;
  });

  const [autoDarkMode, setAutoDarkMode] = useState(() => {
    // Désactivé pour la période de test
    return false;
  });

  const [currentTheme, setCurrentTheme] = useState(() => {
    // PÉRIODE DE TEST: Forcer le thème gris
    return 'gris';
  });

  // Atelier Suisse mode - new minimalist design
  const [atelierMode, setAtelierMode] = useState(() => {
    const saved = localStorage.getItem('atelierMode');
    return saved !== null ? JSON.parse(saved) : false; // Disabled by default
  });

  // Toggle Atelier Suisse mode
  const toggleAtelierMode = () => {
    setAtelierMode(prev => {
      const newValue = !prev;
      localStorage.setItem('atelierMode', JSON.stringify(newValue));
      return newValue;
    });
  };

  // Auto dark mode: check every minute if it's after 19h
  useEffect(() => {
    if (!autoDarkMode) return;
    
    const checkTime = () => {
      const hour = new Date().getHours();
      const shouldBeDark = hour >= 19 || hour < 7;
      setDarkMode(shouldBeDark);
    };
    
    // Check immediately
    checkTime();
    
    // Check every minute
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [autoDarkMode]);

  // PÉRIODE DE TEST: Thème gris forcé, pas de chargement backend
  // loadUserTheme désactivé pour forcer le thème gris

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    localStorage.setItem('autoDarkMode', JSON.stringify(autoDarkMode));
    localStorage.setItem('appTheme', currentTheme);
    localStorage.setItem('atelierMode', JSON.stringify(atelierMode));
    
    const theme = themes[currentTheme] || themes.ocre;
    
    // Get root elements
    const html = document.documentElement;
    const body = document.body;
    
    // IMPORTANT: DO NOT reset inline styles - CustomizationContext manages color palette CSS variables
    // Only reset classes, preserve CSS custom properties set by CustomizationContext
    html.className = '';
    body.className = '';
    
    // STEP 2: Determine colors based on dark mode and atelier mode
    // For dark mode, use theme colors. For light mode, let CustomizationContext handle colors
    let bgColor, bgGradient, textColor, cardColor, cardBorderColor, textMutedColor;
    
    if (atelierMode) {
      // Atelier Suisse colors override everything
      bgColor = darkMode ? atelierSuisse.colors.backgroundDark : atelierSuisse.colors.background;
      bgGradient = bgColor; // No gradients in Atelier mode
      textColor = darkMode ? atelierSuisse.colors.textDark : atelierSuisse.colors.text;
      cardColor = darkMode ? atelierSuisse.colors.cardDark : atelierSuisse.colors.card;
      cardBorderColor = darkMode ? atelierSuisse.colors.borderDark : atelierSuisse.colors.border;
      textMutedColor = darkMode ? atelierSuisse.colors.textMutedDark : atelierSuisse.colors.textMuted;
    } else if (darkMode) {
      // Dark mode uses theme's dark palette
      bgColor = theme.dark.bg;
      bgGradient = theme.dark.bgGradient || theme.dark.bg;
      textColor = theme.dark.text;
      cardColor = theme.dark.card;
      cardBorderColor = theme.dark.cardBorder;
      textMutedColor = theme.dark.textMuted;
    } else {
      // LIGHT MODE: Use CustomizationContext colors (already set as CSS variables)
      // Just read from CSS variables, don't overwrite
      bgColor = getComputedStyle(html).getPropertyValue('--color-background').trim() || theme.bg;
      bgGradient = bgColor;
      textColor = getComputedStyle(html).getPropertyValue('--color-text').trim() || theme.text;
      cardColor = getComputedStyle(html).getPropertyValue('--color-card').trim() || theme.card;
      cardBorderColor = getComputedStyle(html).getPropertyValue('--color-border').trim() || theme.cardBorder;
      textMutedColor = getComputedStyle(html).getPropertyValue('--color-textMuted').trim() || theme.textMuted;
    }
    
    // Get primary/accent from CustomizationContext or fall back to theme
    const primaryColor = getComputedStyle(html).getPropertyValue('--color-primary').trim() || theme.primary;
    const accentColor = getComputedStyle(html).getPropertyValue('--color-accent').trim() || theme.accent;
    
    // STEP 3: Apply CSS custom properties for ThemeContext-specific variables
    // These are additional variables, NOT replacing CustomizationContext's --color-* variables
    const cssVars = `
      --theme-bg: ${bgColor};
      --theme-bg-gradient: ${bgGradient};
      --theme-card: ${cardColor};
      --theme-card-border: ${cardBorderColor};
      --theme-text: ${textColor};
      --theme-text-muted: ${textMutedColor};
      --theme-primary: ${atelierMode ? atelierSuisse.colors.primary : primaryColor};
      --theme-accent: ${atelierMode ? atelierSuisse.colors.accent : accentColor};
      --atelier-shadow: ${atelierMode ? (darkMode ? atelierSuisse.shadows.cardDark : atelierSuisse.shadows.card) : 'none'};
      --atelier-shadow-hover: ${atelierMode ? (darkMode ? atelierSuisse.shadows.cardHoverDark : atelierSuisse.shadows.cardHover) : 'none'};
      --atelier-radius: ${atelierMode ? atelierSuisse.borderRadius.none : '12px'};
      --atelier-radius-sm: ${atelierMode ? atelierSuisse.borderRadius.sm : '8px'};
      --atelier-font-heading: ${atelierMode ? atelierSuisse.fonts.headings : "'Inter', sans-serif"};
      --atelier-font-body: ${atelierMode ? atelierSuisse.fonts.body : "'Inter', sans-serif"};
      --atelier-font-mono: ${atelierMode ? atelierSuisse.fonts.mono : "monospace"};
    `;
    
    // STEP 4: Get existing style from html (CustomizationContext's CSS variables) and MERGE with theme vars
    const existingStyle = html.getAttribute('style') || '';
    // Parse existing style to preserve CustomizationContext variables
    const preservedVars = existingStyle.split(';')
      .filter(s => s.trim().startsWith('--color-') || s.trim().startsWith('--primary') || 
                   s.trim().startsWith('--background') || s.trim().startsWith('--border') ||
                   s.trim().startsWith('--foreground') || s.trim().startsWith('--card') ||
                   s.trim().startsWith('--muted') || s.trim().startsWith('--accent') ||
                   s.trim().startsWith('--popover') || s.trim().startsWith('--ring') ||
                   s.trim().startsWith('--input') || s.trim().startsWith('--font-') ||
                   s.trim().startsWith('--radius-') || s.trim().startsWith('--border-width') ||
                   s.trim().startsWith('--shadow-') || s.trim().startsWith('--spacing-') ||
                   s.trim().startsWith('--texture-') || s.trim().startsWith('--layout-') ||
                   s.trim().startsWith('--transition-') || s.trim().startsWith('--neumo-') ||
                   s.trim().startsWith('--table-'))
      .join(';');
    
    // Combine preserved CustomizationContext vars with ThemeContext vars
    html.setAttribute('style', preservedVars + ';' + cssVars);
    
    // STEP 5: Apply background to body - only override in dark mode or atelier mode
    if (darkMode || atelierMode) {
      body.setAttribute('style', `
        background: ${bgGradient} !important;
        min-height: 100vh !important;
        color: ${textColor} !important;
        font-family: var(--atelier-font-body) !important;
      `);
    }
    // In light mode without atelier, let CustomizationContext's body.style.backgroundColor persist
    
    // STEP 6: Add theme-specific class
    body.classList.add(`theme-${currentTheme}`);
    
    // STEP 7: Add dark mode class if enabled
    if (darkMode) {
      html.classList.add('dark');
      body.classList.add('dark');
    }
    
    // STEP 8: Add Atelier Suisse class if enabled
    if (atelierMode) {
      html.classList.add('atelier-suisse');
      body.classList.add('atelier-suisse');
    }

    // STEP 9: Apply glassmorphism for iPhone theme
    if (currentTheme === 'iphone') {
      html.style.setProperty('--theme-card-blur', theme.cardBlur || '20px');
      html.classList.add('theme-glass');
    }
    
    // Force repaint
    body.offsetHeight;

  }, [darkMode, currentTheme, atelierMode]);

  const toggleDarkMode = () => {
    setAutoDarkMode(false); // Disable auto when manually toggling
    setDarkMode(prev => !prev);
  };

  const toggleAutoDarkMode = () => {
    setAutoDarkMode(prev => {
      const newValue = !prev;
      if (newValue) {
        // Immediately apply auto mode
        const hour = new Date().getHours();
        setDarkMode(hour >= 19 || hour < 7);
      }
      return newValue;
    });
  };

  const changeTheme = async (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('appTheme', themeName);
      
      // Save to backend if logged in
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.put(`${API}/user/theme`, { theme: themeName });
        }
      } catch (error) {
        console.log('Could not save theme to backend');
      }
    }
  };

  const getTheme = () => themes[currentTheme] || themes.ocre;

  return (
    <ThemeContext.Provider value={{ 
      darkMode, 
      toggleDarkMode,
      autoDarkMode,
      toggleAutoDarkMode,
      currentTheme,
      changeTheme,
      getTheme,
      themes,
      palette: getTheme(),
      // Atelier Suisse mode
      atelierMode,
      toggleAtelierMode,
      atelierSuisse
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
